/**
 * The work list.
 *
 * Everything here was raised by a detector and will be closed by one when the
 * problem goes away, so a row that is still here is still true. That is the
 * property the screen depends on: a queue where fixed things linger until
 * somebody clicks is a queue people stop reading.
 *
 * Which is also why resolving asks for a reason. An exception closed with no
 * explanation is indistinguishable from one closed to tidy the screen.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Loading, Note, Pill } from '../components/ui'

interface ExceptionRow {
  id: string
  kind: string
  severity: string
  status: string
  title: string
  detail: string
  subject_type: string | null
  subject_id: string | null
  amount_paise: number
  age_days: number
  seen_count: number
  snoozed_until: string | null
  resolution: string | null
  context: Record<string, unknown>
}

interface SweepResult {
  raised: number
  still_open: number
  auto_resolved: number
  detectors: string[]
}

const SEVERITY_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 }

export default function Exceptions() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['exceptions'] })

  const rows = useQuery({
    queryKey: ['exceptions'],
    queryFn: () => api.get<ExceptionRow[]>('/exceptions'),
  })

  const sweep = useMutation({
    mutationFn: () => api.post<SweepResult>('/exceptions/sweep'),
    onSuccess: refresh,
  })
  const resolve = useMutation({
    mutationFn: ({ id, resolution }: { id: string; resolution: string }) =>
      api.post<ExceptionRow>(`/exceptions/${id}/resolve`, { resolution }),
    onSuccess: refresh,
  })
  const acknowledge = useMutation({
    mutationFn: (id: string) => api.post<ExceptionRow>(`/exceptions/${id}/acknowledge`),
    onSuccess: refresh,
  })
  const snooze = useMutation({
    mutationFn: ({ id, until }: { id: string; until: string }) =>
      api.post<ExceptionRow>(`/exceptions/${id}/snooze`, { until }),
    onSuccess: refresh,
  })

  if (rows.isLoading) return <Loading what="Loading the work list" />

  const items = [...(rows.data ?? [])].sort(
    (a, b) =>
      (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9) ||
      b.amount_paise - a.amount_paise,
  )
  const high = items.filter((i) => i.severity === 'high').length
  const atRisk = items.reduce((sum, i) => sum + i.amount_paise, 0)

  const askAndResolve = (id: string) => {
    const resolution = window.prompt(
      'What did you do about it? A row closed with no explanation cannot be told apart from one closed to tidy the screen.',
    )
    if (resolution && resolution.trim()) resolve.mutate({ id, resolution })
  }
  const askAndSnooze = (id: string) => {
    const until = window.prompt('Out of sight until when? (YYYY-MM-DD)')
    if (until && until.trim()) snooze.mutate({ id, until: until.trim() })
  }

  return (
    <>
      <ErrorNote
        error={rows.error ?? sweep.error ?? resolve.error ?? snooze.error}
      />

      {sweep.data && (
        <Note kind="info">
          {sweep.data.raised} raised, {sweep.data.still_open} still open,{' '}
          {sweep.data.auto_resolved} closed themselves because the detector stopped
          seeing them.
        </Note>
      )}

      <Card
        title="Needs looking at"
        hint={
          items.length
            ? `${items.length}, ${high} serious, ${formatInr(atRisk)} involved`
            : undefined
        }
        tight
        actions={
          can('compliance.exception.edit') && (
            <button
              className="small primary"
              disabled={sweep.isPending}
              onClick={() => sweep.mutate()}
            >
              Check again
            </button>
          )
        }
      >
        {items.length === 0 ? (
          <div className="empty">
            Nothing outstanding. Run a check if you want to be sure.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>What</th>
                <th className="num">Amount</th>
                <th className="num">Age</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <Pill value={item.severity} /> {item.title}
                    <div className="faint">{item.detail}</div>
                    {item.seen_count > 1 && (
                      <div className="faint">seen {item.seen_count} times</div>
                    )}
                  </td>
                  <td className="num">
                    {item.amount_paise ? (
                      formatInr(item.amount_paise)
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td className="num muted">{item.age_days}d</td>
                  <td>
                    <Pill value={item.status} />
                    {item.snoozed_until && (
                      <div className="faint">back on {item.snoozed_until}</div>
                    )}
                  </td>
                  <td className="num">
                    {can('compliance.exception.edit') ? (
                      <>
                        {item.status === 'open' && (
                          <>
                            <button
                              className="small"
                              onClick={() => acknowledge.mutate(item.id)}
                            >
                              I'm on it
                            </button>{' '}
                          </>
                        )}
                        <button className="small" onClick={() => askAndSnooze(item.id)}>
                          Snooze
                        </button>{' '}
                        <button
                          className="small primary"
                          onClick={() => askAndResolve(item.id)}
                        >
                          Resolve
                        </button>
                      </>
                    ) : (
                      <span className="faint">read only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Note kind="info">
        Everything here was found by a check that runs again each time. When the
        underlying problem goes away, the row closes itself and is recorded as having
        resolved rather than as having been dealt with — the two are different facts.
      </Note>
    </>
  )
}
